import React from "react";
import { GlassyModal } from "../glassy-modal/glassy-modal";
import { GlassyModalHeader } from "@src/components/molecules/glassy-modal-header/glassy-modal-header";
import { HorizontalSeparator } from "@src/components/atoms/horizontal-separator/horizontal-separator";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { ScanLoadingLinearIndicator } from "@src/components/molecules/scan-loading-linear-indicator/scan-loading-linear-indicator";
import styles from "./ota-configuration-upload-modal.module.scss";
import { useSelector } from "react-redux";

import { OtaDrawerDataInterface } from "@src/types/ota-update";
import { formatBytes } from "@src/cloud-connect/utils/format-bytes";
import { RootState } from "@src/reducers/store";
import UploadIcon from "@src/assets/svg/upload-icon.svg";
import UploadSuccessIcon from "@src/assets/svg/check-badge.svg";
import CloseIcon from "@src/assets/svg/close-icon.svg";
import WarningTriangleRed from "@src/assets/svg/warning-triangle-red.svg";
import { useI18N } from "@src/language/language-context";

const cssClassName = "ota-configuration-upload-modal";

const OtaConfigurationUploadModal = ({
  inputValues,
  onClose,
}: {
  inputValues: OtaDrawerDataInterface;
  onClose: () => void;
}) => {
  const i18n = useI18N();
  const configurationUploadModalState = useSelector(
    (state: RootState) =>
      state.yourSlice.configurationUploadModal.configurationUploadModalState,
  );

  const file = inputValues?.file;

  const texts = {
    uploadingConfigurationText: i18n(
      "modules.otaConfigurationUploadModal.uploadingConfigurationText",
    ),
    descriptionStartText: i18n(
      "modules.otaConfigurationUploadModal.descriptionStartText",
    ),
    helperText: i18n("modules.otaConfigurationUploadModal.helperText"),
    uploadingConfiguration: i18n(
      "modules.otaConfigurationUploadModal.uploadingConfiguration",
    ),
    configurationUploadFailedText: i18n(
      "modules.otaConfigurationUploadModal.configurationUploadFailedText",
    ),
    descriptionFailedText: i18n(
      "modules.otaConfigurationUploadModal.descriptionFailedText",
    ),
    helperFailedText: i18n(
      "modules.otaConfigurationUploadModal.helperFailedText",
    ),
    configurationsUploadSuccessText: i18n(
      "modules.otaConfigurationUploadModal.configurationsUploadSuccessText",
    ),
    descriptionSuccessText: i18n(
      "modules.otaConfigurationUploadModal.descriptionSuccessText",
    ),
    helperSuccessText: i18n(
      "modules.otaConfigurationUploadModal.helperSuccessText",
    ),
    fileText: i18n("modules.otaConfigurationUploadModal.fileText"),
    sizeText: i18n("modules.otaConfigurationUploadModal.sizeText"),
  };

  const OtaConfigruationUploadStartState = () => {
    return (
      <div className={styles[`${cssClassName}__content`]}>
        <div className={styles[`${cssClassName}__title`]}>
          <img src={UploadIcon} alt="Upload" width={25} height={25} />
          <p>{texts.uploadingConfigurationText}</p>
        </div>

        <p className={styles[`${cssClassName}__description`]}>
          {texts.descriptionStartText}
        </p>

        <div className={styles[`${cssClassName}__file-info`]}>
          <div className={styles[`${cssClassName}__file-row`]}>
            <span className={styles[`${cssClassName}__file-label`]}>
              {texts.fileText}
            </span>
            <span className={styles[`${cssClassName}__file-value`]}>
              {file?.name}
            </span>
          </div>

          <div className={styles[`${cssClassName}__file-row`]}>
            <span className={styles[`${cssClassName}__file-label`]}>
              {texts.sizeText}
            </span>
            <span className={styles[`${cssClassName}__file-value`]}>
              {formatBytes(file?.size)}
            </span>
          </div>
        </div>

        <small className={styles[`${cssClassName}__helper-text`]}>
          {texts.helperText}
        </small>

        <div
          className={
            styles[`${cssClassName}__connection-loading-indicator-wrapper`]
          }
        >
          <Spacer16 />
          <Spacer16 />
          <ScanLoadingLinearIndicator />
        </div>
      </div>
    );
  };
  const OtaConfigruationUploadErrorState = () => {
    return (
      <div className={styles[`${cssClassName}__content`]}>
        <div className={styles[`${cssClassName}__title`]}>
          <img src={WarningTriangleRed} alt="Error" width={25} height={25} />
          <p>{texts.configurationUploadFailedText}</p>
        </div>

        <p className={styles[`${cssClassName}__description`]}>
          {texts.descriptionFailedText}
        </p>

        <div className={styles[`${cssClassName}__file-info`]}>
          <div className={styles[`${cssClassName}__file-row`]}>
            <span className={styles[`${cssClassName}__file-label`]}>
              {texts.fileText}
            </span>
            <span className={styles[`${cssClassName}__file-value`]}>
              {file?.name}
            </span>
          </div>

          <div className={styles[`${cssClassName}__file-row`]}>
            <span className={styles[`${cssClassName}__file-label`]}>
              {texts.sizeText}
            </span>
            <span className={styles[`${cssClassName}__file-value`]}>
              {formatBytes(file?.size)}
            </span>
          </div>
        </div>

        <small className={styles[`${cssClassName}__helper-text`]}>
          {texts.descriptionFailedText}
        </small>
      </div>
    );
  };
  const OtaConfigruationUploadSuccessState = () => {
    return (
      <div className={styles[`${cssClassName}__content`]}>
        <div className={styles[`${cssClassName}__title`]}>
          <img src={UploadSuccessIcon} alt="Success" width={25} height={25} />
          <p>{texts.configurationsUploadSuccessText}</p>
        </div>

        <p className={styles[`${cssClassName}__description`]}>
          {texts.descriptionSuccessText}
        </p>

        <div className={styles[`${cssClassName}__file-info`]}>
          <div className={styles[`${cssClassName}__file-row`]}>
            <span className={styles[`${cssClassName}__file-label`]}>
              {texts.fileText}
            </span>
            <span className={styles[`${cssClassName}__file-value`]}>
              {file?.name}
            </span>
          </div>

          <div className={styles[`${cssClassName}__file-row`]}>
            <span className={styles[`${cssClassName}__file-label`]}>
              {texts.sizeText}
            </span>
            <span className={styles[`${cssClassName}__file-value`]}>
              {formatBytes(file?.size)}
            </span>
          </div>
        </div>

        <small className={styles[`${cssClassName}__helper-text`]}>
          {texts.helperSuccessText}
        </small>
      </div>
    );
  };
  const getModalContent = () => {
    const componentStateByStoreState = {
      "configuration-upload-started": OtaConfigruationUploadStartState,
      "configuration-upload-completed": OtaConfigruationUploadSuccessState,
      "configuration-upload-failed": OtaConfigruationUploadErrorState,
    };

    return componentStateByStoreState[configurationUploadModalState]();
  };
  return (
    <div className={styles[cssClassName]}>
      <GlassyModal
        onClickOutside={
          configurationUploadModalState !== "configuration-upload-started" &&
          onClose
        }
      >
        <div className={styles[`${cssClassName}__modal-body`]}>
          <GlassyModalHeader
            title={texts.uploadingConfigurationText}
            rightIcon={
              configurationUploadModalState !==
                "configuration-upload-started" && CloseIcon
            }
            rightIconFunction={
              configurationUploadModalState !==
                "configuration-upload-started" && onClose
            }
          />

          <HorizontalSeparator verticalMargin={16} />

          <Spacer16 />
          {getModalContent()}
        </div>
      </GlassyModal>
    </div>
  );
};

export default OtaConfigurationUploadModal;
